import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { usePerformanceMonitoring } from '../../hooks/usePerformanceMonitoring';

// Module-level flag to prevent double logging in development
let hasLoggedInitialization = false;

interface PerformanceContextType {
  measureRenderTime: (componentName?: string) => number | undefined;
  startRenderTiming: () => void;
  measureApiCall: (url: string, duration: number, success: boolean) => void;
  getPerformanceSummary: () => {
    avgLoadTime: number;
    avgRenderTime: number;
    avgMemoryUsage: number;
    totalMeasurements: number;
  } | null;
}

const PerformanceContext = createContext<PerformanceContextType | null>(null);

interface PerformanceProviderProps {
  children: ReactNode;
  enabled?: boolean;
  logToConsole?: boolean;
  thresholds?: {
    loadTime?: number;
    renderTime?: number;
    memoryUsage?: number;
  };
}

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({
  children,
  enabled = true,
  logToConsole = false,
  thresholds,
}) => {
  if (logToConsole && !hasLoggedInitialization) {
    // PerformanceProvider rendering
  }

  const performanceMonitoring = usePerformanceMonitoring({
    enabled,
    logToConsole,
    thresholds,
  });

  if (logToConsole && !hasLoggedInitialization) {
    // PerformanceProvider: performanceMonitoring initialized
    hasLoggedInitialization = true;
  }

  // Log performance summary periodically in development
  useEffect(() => {
    if (!enabled || !logToConsole) return;

    const interval = setInterval(() => {
      const summary = performanceMonitoring.getPerformanceSummary();
      if (summary) {
        // Performance summary logged
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [enabled, logToConsole, performanceMonitoring]);

  return (
    <PerformanceContext.Provider value={performanceMonitoring}>
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformanceContext = (): PerformanceContextType => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformanceContext must be used within a PerformanceProvider');
  }
  return context;
};

// Component wrapper for automatic performance monitoring
export const PerformanceMonitoredComponent: React.FC<{
  children: ReactNode;
  name: string;
}> = ({ children, name }) => {
  const { startRenderTiming, measureRenderTime } = usePerformanceContext();

  useEffect(() => {
    startRenderTiming();
    return () => {
      measureRenderTime(name);
    };
  }, [startRenderTiming, measureRenderTime, name]);

  return <>{children}</>;
};